// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Presentation contracts owned by the BFF boundary.
 *
 * These are read models the BFF composes for one administration screen. They
 * are **not** cross-module contracts: root `shared/` remains the sole owner of
 * those (`RG-019.1`), and every domain-bearing member below reuses a validator
 * from {@link ./schemas} rather than restating a backend model
 * (`CST-016`: the UI does not duplicate backend domain rules).
 *
 * A read model exists here only when a screen genuinely needs a composition
 * that no single project contract expresses — a dashboard roll-up, an approval
 * request, a connector-plus-health pair. Each is validated at the trust
 * boundary like any other external input.
 */

import { z } from 'zod';

import {
  agentMetadataSchema,
  auditActorSchema,
  auditOutcomeSchema,
  citationSchema,
  connectorHealthSchema,
  connectorMetadataSchema,
  dataClassificationSchema,
  mcpServerSchema,
  policyEffectSchema,
  roleSchema,
  userProfileSchema,
} from './schemas';

/**
 * Minimised audit fact for administration screens.
 *
 * The project `AuditEvent` contract carries a full `AuditContext`, including
 * the originating `IdentityContext`. `DAT-006` and `OPX-001` require data
 * minimisation in operational surfaces, and `SEC2-050` prohibits routing
 * protected identity material into an administrative view that does not need
 * it. The BFF therefore projects each audit fact down to the fields an
 * administrator reads, keeping the actor as the real `AuditActor` contract type
 * and reducing the remainder to safe scalars.
 *
 * Correlation identifiers are retained so an operator can join a UI observation
 * to the authoritative audit record (`OPS-051`).
 */
export const auditEventViewSchema = z.object({
  event_id: z.string(),
  event_type: z.string(),
  action: z.string(),
  actor: auditActorSchema,
  outcome: auditOutcomeSchema.default('unknown'),
  reason: z.string().nullable().default(null),
  timestamp: z.string(),
  resource_type: z.string().nullable().default(null),
  resource_label: z.string().nullable().default(null),
  correlation_id: z.string().nullable().default(null),
});

/** Minimised audit fact for administration screens. */
export type AuditEventView = z.infer<typeof auditEventViewSchema>;

/** Operational health rating shared by monitoring and health screens. */
export const healthStateSchema = z.enum(['healthy', 'degraded', 'unhealthy', 'unknown']);

/** Operational health rating. */
export type HealthState = z.infer<typeof healthStateSchema>;

/**
 * A headline figure on a dashboard.
 *
 * `ADM-014` forbids rendering missing telemetry as zero or healthy, so the
 * value is explicitly nullable and `observed` records whether the BFF actually
 * obtained a measurement.
 */
export const summaryMetricSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.number().nullable().default(null),
  unit: z.string().nullable().default(null),
  observed: z.boolean(),
  health: healthStateSchema.default('unknown'),
});

/** A headline figure on a dashboard. */
export type SummaryMetric = z.infer<typeof summaryMetricSchema>;

/** Dashboard roll-up for either application. */
export const dashboardSummarySchema = z.object({
  generated_at: z.string(),
  scope_label: z.string(),
  metrics: z.array(summaryMetricSchema).default([]),
  degraded_reasons: z.array(z.string()).default([]),
});

/** Dashboard roll-up. */
export type DashboardSummary = z.infer<typeof dashboardSummarySchema>;

/**
 * The control-plane dashboard summary, exactly as Platform publishes it.
 *
 * `GET /api/v1/control-plane/summary` answers `ControlPlaneSummary` — four
 * non-negative integers counted from the stored collections, on a model that
 * forbids extra members
 * (`platform/src/eai_platform/runtime/runtime_gateway/control_plane_reads.py`).
 * The BFF proxies that route without transforming the body
 * (`ui/bff/.../routes/resources.py`), so this is the shape the browser receives.
 *
 * The authoritative contract bundle (`shared/generated/openapi/openapi.json`)
 * publishes no summary schema and no summary path, and no accepted document
 * states a different one, so the Platform response model is authoritative here
 * and this validator mirrors it field for field.
 *
 * Deliberately *not* {@link dashboardSummarySchema}: that richer shape — an
 * observation timestamp, a scope label, self-describing metrics, degraded
 * reasons — is what a summary endpoint would have to publish for the UI to
 * report observability the way `ADM-014` wants. Platform does not publish it,
 * and validating against it is what left the dashboard unable to read a
 * perfectly valid response. Requiring the UI's preferred shape does not make
 * the service produce it.
 */
export const controlPlaneSummarySchema = z.object({
  tenants: z.number().int().nonnegative(),
  ai_services: z.number().int().nonnegative(),
  documents: z.number().int().nonnegative(),
  approvals_pending: z.number().int().nonnegative(),
});

/** Control-plane dashboard counters. */
export type ControlPlaneSummary = z.infer<typeof controlPlaneSummarySchema>;

/** A registered AI service: an agent or an MCP server, with its route status. */
export const aiServiceSchema = z.object({
  service_id: z.string(),
  kind: z.enum(['agent', 'mcp_server']),
  agent: agentMetadataSchema.nullable().default(null),
  mcp_server: mcpServerSchema.nullable().default(null),
  enabled: z.boolean(),
  health: healthStateSchema.default('unknown'),
});

/** A registered AI service. */
export type AiService = z.infer<typeof aiServiceSchema>;

/** A knowledge index generation and its completeness evidence. */
export const knowledgeCollectionSchema = z.object({
  collection_id: z.string(),
  name: z.string(),
  generation: z.string(),
  document_count: z.number().int().nullable().default(null),
  chunk_count: z.number().int().nullable().default(null),
  embedding_model: z.string().nullable().default(null),
  last_indexed_at: z.string().nullable().default(null),
  classification: dataClassificationSchema.nullable().default(null),
  health: healthStateSchema.default('unknown'),
});

/** A knowledge index generation. */
export type KnowledgeCollection = z.infer<typeof knowledgeCollectionSchema>;

/** A governed policy as presented to an administrator. */
export const policyViewSchema = z.object({
  policy_id: z.string(),
  name: z.string(),
  version: z.string(),
  effect: policyEffectSchema,
  scope_label: z.string(),
  enabled: z.boolean(),
  updated_at: z.string().nullable().default(null),
  description: z.string().nullable().default(null),
});

/** A governed policy. */
export type PolicyView = z.infer<typeof policyViewSchema>;

/** Lifecycle state of an approval request. */
export const approvalStateSchema = z.enum([
  'pending',
  'approved',
  'rejected',
  'expired',
  'cancelled',
]);

/** Lifecycle state of an approval request. */
export type ApprovalState = z.infer<typeof approvalStateSchema>;

/**
 * A high-impact action awaiting a human decision.
 *
 * `ARCH-026` requires the reviewer to see the exact proposed action, target,
 * material inputs, side effects, and supporting evidence, and binds the
 * approval to a payload digest so a changed payload invalidates it. Those
 * fields are mandatory here, not optional decoration.
 */
export const approvalRequestSchema = z.object({
  approval_id: z.string(),
  state: approvalStateSchema,
  action: z.string(),
  target_label: z.string(),
  requested_by: z.string(),
  requested_at: z.string(),
  expires_at: z.string().nullable().default(null),
  payload_digest: z.string(),
  policy_version: z.string(),
  material_inputs: z.array(z.object({ label: z.string(), value: z.string() })).default([]),
  side_effects: z.array(z.string()).default([]),
  evidence: z.array(citationSchema).default([]),
  ai_limitations: z.array(z.string()).default([]),
  decided_at: z.string().nullable().default(null),
  decided_by: z.string().nullable().default(null),
});

/** A high-impact action awaiting a human decision. */
export type ApprovalRequest = z.infer<typeof approvalRequestSchema>;

/** Monitored service health with its declared owner and runbook. */
export const serviceHealthSchema = z.object({
  service_id: z.string(),
  name: z.string(),
  health: healthStateSchema,
  observed: z.boolean(),
  release_version: z.string().nullable().default(null),
  owner: z.string().nullable().default(null),
  runbook_url: z.string().nullable().default(null),
  checked_at: z.string().nullable().default(null),
  message: z.string().nullable().default(null),
});

/** Monitored service health. */
export type ServiceHealth = z.infer<typeof serviceHealthSchema>;

/** A delegated administrative grant. */
export const accessGrantSchema = z.object({
  grant_id: z.string(),
  subject_label: z.string(),
  role: roleSchema,
  scope_label: z.string(),
  granted_at: z.string(),
  expires_at: z.string().nullable().default(null),
  granted_by: z.string(),
  break_glass: z.boolean().default(false),
});

/** A delegated administrative grant. */
export type AccessGrant = z.infer<typeof accessGrantSchema>;

/** A registered enterprise data source inside a tenant data plane. */
export const dataSourceSchema = z.object({
  data_source_id: z.string(),
  name: z.string(),
  system_kind: z.string(),
  connector_id: z.string().nullable().default(null),
  classification: dataClassificationSchema.nullable().default(null),
  last_synchronised_at: z.string().nullable().default(null),
  health: healthStateSchema.default('unknown'),
  enabled: z.boolean(),
});

/** A registered enterprise data source. */
export type DataSource = z.infer<typeof dataSourceSchema>;

/** One field-level mapping row. */
export const schemaMappingFieldSchema = z.object({
  source_field: z.string(),
  canonical_field: z.string(),
  transform: z.string().nullable().default(null),
  required: z.boolean().default(false),
});

/** A source-to-canonical schema mapping. */
export const schemaMappingSchema = z.object({
  mapping_id: z.string(),
  name: z.string(),
  version: z.string(),
  data_source_id: z.string(),
  canonical_entity: z.string(),
  fields: z.array(schemaMappingFieldSchema).default([]),
  unmapped_source_fields: z.array(z.string()).default([]),
  approved: z.boolean(),
  updated_at: z.string().nullable().default(null),
});

/** A source-to-canonical schema mapping. */
export type SchemaMapping = z.infer<typeof schemaMappingSchema>;

/** A connector paired with its most recent observed health. */
export const connectorWithHealthSchema = z.object({
  connector: connectorMetadataSchema,
  health: connectorHealthSchema.nullable().default(null),
});

/** A connector paired with its most recent observed health. */
export type ConnectorWithHealth = z.infer<typeof connectorWithHealthSchema>;

/** A tenant-scoped health check result. */
export const healthCheckSchema = z.object({
  check_id: z.string(),
  name: z.string(),
  health: healthStateSchema,
  observed: z.boolean(),
  detail: z.string().nullable().default(null),
  checked_at: z.string().nullable().default(null),
});

/** A tenant-scoped health check result. */
export type HealthCheck = z.infer<typeof healthCheckSchema>;

/**
 * Session state as reported by the BFF.
 *
 * `capabilities` drives which controls are *offered*. `CST-048` and
 * `07-SECURITY.md` §15.4 keep enforcement on the server: hiding a control is a
 * usability affordance, never an authorization decision.
 */
export const sessionViewSchema = z.object({
  authenticated: z.boolean(),
  subject_id: z.string().nullable().default(null),
  profile: userProfileSchema.nullable().default(null),
  tenant_id: z.string().nullable().default(null),
  tenant_label: z.string().nullable().default(null),
  university_label: z.string().nullable().default(null),
  roles: z.array(z.string()).default([]),
  capabilities: z.array(z.string()).default([]),
  expires_at: z.string().nullable().default(null),
  serving_mode: z.enum(['platform_only', 'agents_connected']).nullable().default(null),
  degraded: z.boolean().default(false),
});

/** Session state as reported by the BFF. */
export type SessionView = z.infer<typeof sessionViewSchema>;

/** Outcome envelope returned when an approval decision is recorded. */
export const approvalDecisionResultSchema = z.object({
  approval: approvalRequestSchema,
  audit_event_id: z.string(),
  outcome: auditOutcomeSchema,
});

/** Outcome of recording an approval decision. */
export type ApprovalDecisionResult = z.infer<typeof approvalDecisionResultSchema>;
